import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ModalBody from '../../Modal/ModalBody';
import Button from '../../Button/Button';
import SectionLoader from '../../Loader/SectionLoader';
import ProductImage from '../../LegacyOrder/ProductImage';
import { ReactComponent as ClockBlue } from '../../../assets/images/svg/clock-blue.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/images/svg/download.svg';
import {
  getCurrentAccount,
  getCurrentAccountUid,
} from '../../../store/selectors/accountSelectors';
import {
  selectPortfolioSingleTransaction,
  selectPortfolioSingleTransactionError,
} from '../../../store/selectors/portfolioTransactionsSelector';
import { selectIsLoadingByActionType } from '../../../store/selectors/loadingSelectors';
import {
  portfolioTransactionsSingleFetch,
  emptyPortfolioTransactionSingle,
} from '../../../store/actions/portfolio/portfolioActions';
import { PORTFOLIO_TRANSACTIONS_SINGLE_LOADING } from '../../../store/actions/portfolio/portfolioActionConstants';
import { formatDateTime } from '../../../util/helpers/dateHelpers';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import { PAYMENT_METHOD_TYPES } from '../../../util/enum/api/paymentTypes';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';

const SUBTITLE_COLOR = '#333333';

const HeroSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${pxToRem(8)};
  padding: ${pxToRem(8)} 0 ${pxToRem(16)};
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
`;

const HeroImage = styled.div`
  > img {
    width: ${pxToRem(80)};
    height: ${pxToRem(80)};
    object-fit: contain;
    margin: 0;
  }
`;

const HeroCaption = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  font-weight: 700;
  color: ${SUBTITLE_COLOR};
  text-align: center;
`;

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${pxToRem(12)} 0;
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
`;

const DetailRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${pxToRem(12)};
  padding: ${pxToRem(6)} 0;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
`;

const DetailLabel = styled.span`
  color: ${themeColors.colorTextSecondary};
`;

const DetailValue = styled.span`
  color: ${themeColors.colorTextPrimary};
  font-weight: 500;
  text-align: right;
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(12)};
  padding: ${pxToRem(16)} 0;
`;

const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(6)};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorPrimary};
  text-decoration: underline;

  > svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(12)};
  margin-top: ${pxToRem(8)};

  > button {
    width: 100%;
  }
`;

const DownloadButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(6)};

  > svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
  }
`;

const FetchErrorText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(13)};
  color: ${themeColors.colorError};
  text-align: center;
  margin: ${pxToRem(8)} 0 0;
`;

const formatPaymentMaskedNumber = (paymentMethod) => {
  if (!paymentMethod) return '';
  if (paymentMethod.PaymentMethodType === PAYMENT_METHOD_TYPES.CREDIT_CARD) {
    return paymentMethod.Last4Digits || '';
  }
  if (paymentMethod.PaymentMethodType === PAYMENT_METHOD_TYPES.ACH) {
    return paymentMethod.BankAccountMask || '';
  }
  return '';
};

const SellFromHomeTransactionDetails = ({
  selectedHit,
  quantity,
  lockedSnapshot,
  handleClose,
  handleViewHistory,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const account = useSelector(getCurrentAccount);
  const accountUid = useSelector(getCurrentAccountUid);
  const singleTransaction = useSelector(selectPortfolioSingleTransaction);
  const singleTransactionError = useSelector(
    selectPortfolioSingleTransactionError,
  );
  const isFetchingTransaction = useSelector(
    selectIsLoadingByActionType(PORTFOLIO_TRANSACTIONS_SINGLE_LOADING),
  );

  const transactionUid = lockedSnapshot?.transactionUid;

  useEffect(() => {
    if (!accountUid || !transactionUid) return undefined;
    dispatch(portfolioTransactionsSingleFetch({ accountUid, transactionUid }));
    return () => {
      dispatch(emptyPortfolioTransactionSingle());
    };
  }, [accountUid, transactionUid, dispatch]);

  const productCaption = `${selectedHit?.WeightCaption ?? ''} ${
    selectedHit?.Caption ?? ''
  }`.trim();

  // Prefer backend values when available; fall back to the locked snapshot for
  // fields the GET response doesn't currently expose (per-fee breakdown,
  // pre-auth amount, payment-method mask).
  const unitPrice = lockedSnapshot?.unitPrice ?? 0;
  const preAuthAmount = lockedSnapshot?.preAuthAmount ?? 0;
  const shippingFee = lockedSnapshot?.shippingFee ?? 0;
  const insuranceFee = lockedSnapshot?.insuranceFee ?? 0;
  const paymentMethod = lockedSnapshot?.paymentMethod;
  const last4 = formatPaymentMaskedNumber(paymentMethod);

  const backendAmount = Number(singleTransaction?.Amount);
  const payout =
    Number.isFinite(backendAmount) && backendAmount > 0
      ? backendAmount
      : lockedSnapshot?.payout ?? 0;
  const submittedAt =
    singleTransaction?.DateTime || lockedSnapshot?.submittedAt;
  const referenceNumber =
    singleTransaction?.Uid || lockedSnapshot?.referenceNumber || '';
  const statusLabel = singleTransaction?.StatusType
    ? t(
        `sellWizard.sfhWizard.transactionDetails.status_${singleTransaction.StatusType}`,
        {
          defaultValue: singleTransaction.StatusType,
        },
      )
    : t('sellWizard.sfhWizard.transactionDetails.statusRequestSubmitted');

  const showLoader =
    isFetchingTransaction && !singleTransaction && !singleTransactionError;

  const onViewHistoryClick = () => {
    if (typeof handleViewHistory === 'function') handleViewHistory();
  };

  const handleDownloadPackingSlip = () => {
    // TODO(SFH packing slip download): wire up once the backend exposes the
    // packing-slip download URL on the SFH order resource.
  };

  const handleDownloadShippingLabel = () => {
    // TODO(SFH shipping label download): wire up once the backend exposes
    // the shipping-label download URL on the SFH order resource.
  };

  return (
    <SectionLoader isLoading={showLoader}>
      <ModalBody>
        <HeroSection>
          <HeroImage>
            <ProductImage
              productCode={selectedHit?.Code}
              width={80}
              height={80}
            />
          </HeroImage>
          <HeroCaption>{productCaption}</HeroCaption>
        </HeroSection>

        <DetailsSection>
          <DetailRow>
            <DetailLabel>
              {t('sellWizard.sfhWizard.transactionDetails.summaryLabel')}
            </DetailLabel>
            <DetailValue>
              {t('sellWizard.sfhWizard.transactionDetails.summaryValue', {
                count: quantity,
              })}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('sellWizard.sfhWizard.transactionDetails.transactedOn')}
            </DetailLabel>
            <DetailValue>
              {submittedAt ? formatDateTime(submittedAt) : ''}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('sellWizard.sfhWizard.transactionDetails.referenceNumber')}
            </DetailLabel>
            <DetailValue>{referenceNumber}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('sellWizard.sfhWizard.transactionDetails.status')}
            </DetailLabel>
            <DetailValue>{statusLabel}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('sellWizard.sfhWizard.transactionDetails.submitter')}
            </DetailLabel>
            <DetailValue>{account?.DisplayName?.trim() || ''}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('sellWizard.sfhWizard.transactionDetails.shippingMethod')}
            </DetailLabel>
            <DetailValue>
              {t('sellWizard.sfhWizard.transactionDetails.shippingMethodValue')}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('sellWizard.sfhWizard.transactionDetails.pricePerUnit')}
            </DetailLabel>
            <DetailValue>{formatMoneyNumeral(unitPrice)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('sellWizard.sfhWizard.transactionDetails.orderTotal')}
            </DetailLabel>
            <DetailValue>{formatMoneyNumeral(payout)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('sellWizard.sfhWizard.transactionDetails.preAuthAmount')}
            </DetailLabel>
            <DetailValue>{formatMoneyNumeral(preAuthAmount)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('sellWizard.sfhWizard.transactionDetails.shippingFee')}
            </DetailLabel>
            <DetailValue>{formatMoneyNumeral(shippingFee)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('sellWizard.sfhWizard.transactionDetails.insuranceFee')}
            </DetailLabel>
            <DetailValue>{formatMoneyNumeral(insuranceFee)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('sellWizard.sfhWizard.transactionDetails.creditCard')}
            </DetailLabel>
            <DetailValue>
              {last4
                ? t('sellWizard.sfhWizard.transactionDetails.creditCardValue', {
                    last4,
                  })
                : ''}
            </DetailValue>
          </DetailRow>
        </DetailsSection>

        <LinkRow>
          <LinkButton
            type="button"
            onClick={onViewHistoryClick}
            data-cy="link-sfh-view-history"
          >
            <ClockBlue />
            {t('sellWizard.sfhWizard.transactionDetails.viewHistory')}
          </LinkButton>
          <LinkButton
            type="button"
            onClick={handleDownloadPackingSlip}
            data-cy="link-sfh-download-packing-slip"
          >
            <DownloadIcon />
            {t('sellWizard.sfhWizard.transactionDetails.downloadPackingSlip')}
          </LinkButton>
        </LinkRow>

        {singleTransactionError && (
          <FetchErrorText data-cy="text-sfh-transaction-fetch-error">
            {t('sellWizard.sfhWizard.transactionDetails.fetchFailed')}
          </FetchErrorText>
        )}

        <Footer>
          <Button
            variant="primaryOutlined"
            textTransform="none"
            onClick={handleDownloadShippingLabel}
            data-cy="button-sfh-download-shipping-label"
          >
            <DownloadButtonContent>
              <DownloadIcon />
              {t(
                'sellWizard.sfhWizard.transactionDetails.downloadShippingLabel',
              )}
            </DownloadButtonContent>
          </Button>
          <Button
            variant="primaryOutlined"
            textTransform="none"
            onClick={handleClose}
            data-cy="button-sfh-transaction-close"
          >
            {t('sellWizard.sfhWizard.transactionDetails.close')}
          </Button>
        </Footer>
      </ModalBody>
    </SectionLoader>
  );
};

SellFromHomeTransactionDetails.propTypes = {
  selectedHit: PropTypes.shape({
    Code: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
  }),
  quantity: PropTypes.number,
  lockedSnapshot: PropTypes.shape({
    unitPrice: PropTypes.number,
    payout: PropTypes.number,
    preAuthAmount: PropTypes.number,
    shippingFee: PropTypes.number,
    insuranceFee: PropTypes.number,
    submittedAt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
      PropTypes.number,
    ]),
    referenceNumber: PropTypes.string,
    transactionUid: PropTypes.string,
    paymentMethod: PropTypes.shape({
      PaymentMethodType: PropTypes.number,
      Last4Digits: PropTypes.string,
      BankAccountMask: PropTypes.string,
    }),
  }),
  handleClose: PropTypes.func,
  handleViewHistory: PropTypes.func,
};

export default SellFromHomeTransactionDetails;
